import React, { DragEvent, DragEventHandler, PropsWithChildren, useState } from 'react';
import { connect } from 'react-redux';
import { DropArea } from '@ff-os/shared-react/components/file-uploader/DropArea';
import { createFileUploaderRef, UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { maximumImageSize } from '@ff-os/shared-utils/constants/maximum-file-sizes';
import { PhotoPrintsPdpDispatch } from '@ff-scene-plugins/photo-prints-redux/store';
import { ChangeVariantConfiguration } from '../../thunks/saveVariant';
import { uploadImages } from '../../thunks/uploadImages';
import { incrementWithScheduledReset } from '../../thunks/withScheduledReset';
import { ChangeVariantWindow } from '../change-variant-window/ChangeVariantWindow';
import { FotofunPanel } from '../fotofun/FotofunPanel';
import styles from './PhotoPrintsDropzone.module.scss';

const _PhotoPrintsDropzone = ({ children, uploadFilesInto, onError, fotofunPanelRef, photoPrintGroupsRef }: OwnProps & Props) => {

    const [isVariantModalOpen, setVariantModalOpen] = useState(false);
    const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);

    const [files, setFiles] = useState<File[]>([]);
    const [errors, setErrors] = useState<UploadError[]>([]);

    const [photoId, setPhotoId] = useState<string | null>(null);
    const [sourceGroupId, setSourceGroupId] = useState<string | null>(null);

    const isMovingFiles = (event: DragEvent<Element>) => {
        return event.dataTransfer.items[0]?.kind === 'file';
    };

    const uploaderRef = createFileUploaderRef();

    const onDrop: DragEventHandler = (event) => {

        event.preventDefault();

        if (!isMovingFiles(event)) {
            const photoId = event.dataTransfer.getData('photoId');
            const sourceGroupId = event.dataTransfer.getData('groupId');
            if (photoId) {
                setPhotoId(photoId);
                setSourceGroupId(sourceGroupId);
                setVariantModalOpen(true);
            }
        } else {
            setIsCreateGroupOpen(true);
        }
    };

    const onSave = ({ groupId }: ChangeVariantConfiguration) => {
        uploadFilesInto(groupId, files);
        if (errors.length) {
            onError(groupId, errors);
            setErrors([]);
        }
    };

    return (
        <>
            <DropArea
                className={styles.default}
                onDrop={onDrop}
                ref={uploaderRef}
                maxSize={maximumImageSize}
                multiple={true}
                onError={setErrors}
                onChange={setFiles}>

                <div ref={photoPrintGroupsRef}>
                    {children}
                </div>
            </DropArea>

            <FotofunPanel ref={fotofunPanelRef} />

            {isVariantModalOpen && photoId && (
                <WindowStackItem>
                    <ChangeVariantWindow withMethod forceNewGroup
                        close={() => setVariantModalOpen(false)}
                        photoId={photoId}
                        sourceGroupId={sourceGroupId || undefined} />
                </WindowStackItem>
            )}

            {isCreateGroupOpen && (
                <WindowStackItem>
                    <ChangeVariantWindow
                        title='photo_prints.extra_group'
                        close={() => setIsCreateGroupOpen(false)}
                        onSave={onSave} />
                </WindowStackItem>
            )}
        </>
    );
};

type OwnProps = PropsWithChildren<{
    fotofunPanelRef: React.RefObject<HTMLDivElement>;
    photoPrintGroupsRef: React.RefObject<HTMLDivElement>;
}>;

type Props = {
    uploadFilesInto: (groupId: string, files: File[]) => void;
    onError: (groupId: string, errors: UploadError[]) => void;
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, _: OwnProps) => ({
    uploadFilesInto: (groupId: string, files: File[]) => dispatch(uploadImages(files, groupId)),
    onError: (groupId: string, errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(groupId, 'failedUploadsCount', errors.length));
    }
});

export const PhotoPrintsDropzone = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsDropzone);